import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import { CrawledPostManangementData } from '@/Interfaces/I_postManagement';
import { getCrawledPost, patchCrawledPost } from '@/Redux/slices/CrawledPostManagementSlice';
import { timeTranslate } from '@/Utils/timeTranslate';
import { Button, DatePicker, Empty, Input, Popconfirm, Select, Table, Tooltip } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';

interface SorterDataType {
  date: number;
}

interface CrawledPostUIData {
  id: number;
  title: string;
  userName: string;
  content: string;
  url: string;
  uuid: string;
  medium: string;
  topic: string;
  date: string;
  action: { disabled: boolean; postUuid: string; medium: string };
}

const { RangePicker } = DatePicker;
const { Search } = Input;

type RangeValue = [Dayjs | null, Dayjs | null] | null;
const dateFrom = dayjs().subtract(30, 'days'); // set default date range 30 days
const dateTo = dayjs();
const defaultDateRange: [Dayjs, Dayjs] = [dateFrom, dateTo];

const CrawledPost = () => {
  const dispatch = useAppDispatch();
  const postData = useAppSelector((state) => state.crawledPostManagement.value.crawledPost);
  const loading = useAppSelector((state) => state.crawledPostManagement.loading);
  const [dates, setDates] = useState<RangeValue>(defaultDateRange);
  const [data, setData] = useState([] as CrawledPostUIData[]);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<SorterDataType>>({
    order: 'descend',
    columnKey: 'date',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPage, setTotalPage] = useState(10);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchOption, setSearchOption] = useState('content');

  const searchOptions = [
    {
      value: 'content',
      label: 'Post Content',
    },
    {
      value: 'userName',
      label: 'User Name',
    },
    {
      value: 'uuid',
      label: 'Uuid',
    },
    {
      value: 'medium',
      label: 'Medium',
    },
  ];

  const columns: any[] = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: '5%',
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      width: '10%',
    },
    {
      title: 'Post Title',
      dataIndex: 'title',
      key: 'title',
      width: '10%',
      render: (title: string, record: CrawledPostUIData) => {
        const { url } = record;
        const displayTitle = title || url || record.uuid;
        return (
          <Tooltip placement="leftTop" title={displayTitle}>
            <div className="flex">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="line-clamp-3 break-all mb-0"
              >
                {displayTitle}
              </a>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Post Content',
      dataIndex: 'content',
      key: 'content',
      width: '10%',
      render: (content: string) => (
        <Tooltip placement="leftTop" title={content}>
          <p className="line-clamp-3 break-all mb-0">{content}</p>
        </Tooltip>
      ),
    },
    {
      title: 'Uuid',
      dataIndex: 'uuid',
      key: 'uuid',
      width: '8%',
    },
    {
      title: 'Medium',
      dataIndex: 'medium',
      key: 'medium',
      width: '8%',
    },
    {
      title: 'Topic',
      dataIndex: 'topic',
      key: 'topic',
      width: '5%',
    },
    {
      title: 'Creation Date',
      dataIndex: 'date',
      key: 'date',
      width: '10%',
      render: (date: number) => <div className="flex">{timeTranslate(date)}</div>,
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'date' ? sortedInfo.order : null,
    },
    {
      title: 'Action',
      key: 'action',
      dataIndex: 'action',
      width: '10%',
      className: 'pr-0',
      render: (item: { postUuid: string; medium: string; disabled: boolean }) => {
        const { postUuid, medium, disabled } = item;
        return (
          <div className="flex justify-between items-center">
            <div style={{ width: 77.5 }} className="flex justify-center">
              <Popconfirm
                title={disabled ? 'Show Crawled Post? ' : 'Hide Crawled Post?'}
                onConfirm={() => onHideClick(postUuid, medium, disabled)}
                okText="Yes"
                cancelText="No"
              >
                {disabled ? <Button>Enable</Button> : <Button danger>Disable</Button>}
              </Popconfirm>
            </div>
          </div>
        );
      },
    },
  ];

  const searchPosts = () => {
    const sort =
      sortedInfo.column !== undefined
        ? (sortedInfo.columnKey as string)
            .replace(/[A-Z]/g, (letter) => `_${letter}`)
            .toUpperCase()
            .concat(sortedInfo.order === 'ascend' ? '_ASC' : '_DESC')
        : 'DATE_DESC';
    if (dates && dates[0] && dates[1]) {
      dispatch(
        getCrawledPost({
          content: searchOption === 'content' ? searchValue : null,
          userName: searchOption === 'userName' ? searchValue : null,
          uuid: searchOption === 'uuid' ? searchValue : null,
          medium: searchOption === 'medium' ? searchValue : null,
          sort,
          pageNo: currentPage - 1,
          pageSize: pageSize,
          dateFrom: dates[0].format('YYYY-MM-DD'),
          dateTo: dates[1].format('YYYY-MM-DD'),
        }),
      );
    }
  };

  const disabledDate = (current: any) => {
    return current && current > dayjs().endOf('day');
  };

  const onHideClick = useCallback(
    (postUuid: string, medium: string, disabled: boolean) => {
      const actionString = disabled ? 'ENABLE' : 'DISABLE';
      dispatch(
        patchCrawledPost({
          postUuid,
          medium,
          action: actionString,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const newMapData = postData.data.map((item: CrawledPostManangementData, index: number) => ({
      id: item.id,
      title: item.title,
      userName: item.userName,
      content: item.description,
      url: item.url,
      uuid: item.uuid,
      medium: item.medium,
      topic: item.topic,
      date: item.creationDate,
      action: { disabled: item.disabled, postUuid: item.uuid, medium: item.medium },
    }));
    setData(newMapData);
    setTotalPage(postData.pagination.total);
  }, [postData, totalPage, loading]);

  useEffect(() => {
    if (dates && dates[0] && dates[1]) {
      searchPosts();
    }
  }, [dispatch, sortedInfo, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  return (
    <>
      <div className="h-auto bg-white p-6 pb-2">
        <div className="flex justify-between pb-4">
          <div className="flex">
            <div>
              <Select
                style={{ width: 200 }}
                defaultValue={searchOption}
                options={searchOptions}
                onChange={(value) => {
                  setSearchOption(value);
                }}
                bordered={false}
              />
            </div>
            <Search
              placeholder="input text"
              allowClear
              onChange={(value) => {
                setSearchValue(value.target.value.trim());
                setCurrentPage(1);
              }}
              style={{ width: 264 }}
              className="pr-4"
            />
          </div>
          <div className="flex">
            <div className="pr-4">
              <RangePicker
                inputReadOnly
                defaultValue={defaultDateRange}
                disabledDate={disabledDate}
                onCalendarChange={(val) => {
                  setDates(val);
                }}
                disabled={loading}
              />
            </div>
            <Button disabled={loading || !dates || !dates[0] || !dates[1]} onClick={searchPosts}>
              Search
            </Button>
          </div>
        </div>
        {!loading && data.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            onChange={(pagination, filters, sorter) => {
              const sorterInfo = sorter as SorterResult<SorterDataType>;
              setSortedInfo(sorterInfo);
            }}
            pagination={{
              total: totalPage,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              defaultPageSize: pageSize,
              defaultCurrent: 1,
              showSizeChanger: true,
              current: currentPage,
              onChange: (page: number, selectPageSize: number) => {
                setCurrentPage(page);
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
              },
            }}
          />
        )}
      </div>
    </>
  );
};

export default CrawledPost;
